<template>
  <v-layout column>
    <v-flex>
      <v-layout>
        <v-flex xs4 offset-xs8 class="mt-5">
          <v-layout class="pl-1">
            <v-flex shrink align-center style="display: flex;">
              <StatusSelect
                v-model="optionForShowCreatives"
                style="width: 180px;"
              />
            </v-flex>
            <v-flex shrink d-flex align-center>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    :disabled="!reloadable"
                    :loading="loading"
                    @click="removeAllCreatives"
                    small
                    icon
                  >
                    <v-icon small>close</v-icon>
                  </v-btn>
                </template>
                <span>全てのクリエイティブ紐付きを削除する</span>
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex class="mt-1">
      <v-layout>
        <v-flex xs3>
          <AppScrollContainer :height="height">
            <CampaignBudgetPeriodTable
              ref="campaignBudgetPeriodTable"
              @changeBudgetSetting="$emit('changeBudgetSetting')"
              v-model="currentCampaignBudget"
              :items="campaignBudgets"
            />
          </AppScrollContainer>
        </v-flex>
        <v-divider vertical />
        <v-flex xs5>
          <AppScrollContainer :height="height">
            <div :class="fabContainerStyles">
              <CampaignBudgetPeriodTableRow
                ref="campaignBudgetPeriodTableRow"
                @select="setCurrentCampaignBudgetCreativeSchedule"
                @sort="sortDeliverySchedules"
                v-model="currentCampaignBudgetCreativeSchedule"
                :creativeSchedules="currentCampaignBudgetCreativeSchedules"
                :campaignBudget="currentCampaignBudget"
                :isStartTimeEditable="isStartTimeEditable"
              >
                <template v-slot:empty v-if="!currentCampaignBudget">
                  キャンペーン予算期間を選択してください
                </template>
              </CampaignBudgetPeriodTableRow>
            </div>
          </AppScrollContainer>
        </v-flex>
        <v-divider vertical />
        <v-flex xs4>
          <AppScrollContainer :height="height">
            <div :class="fabContainerStyles">
              <CreativeTable
                @copy="copyCreatives"
                @paste="pasteCreatives"
                v-model="currentCreative"
                :items="visibleCreatives"
                :loading="loading"
                :disablePaste="disablePaste"
              >
                <template v-slot:empty>
                  <span v-if="!currentCampaignBudget">
                    キャンペーン予算期間を選択してください
                  </span>
                  <span v-else-if="!currentCampaignBudgetCreativeSchedule">
                    配信スケジュールを選択してください
                  </span>
                  <span v-else-if="!isEndTimeEditable">
                    配信スケジュールが終了しているため変更できません
                  </span>
                  <span v-else>
                    <v-icon small>add</v-icon>
                    ボタンを押してクリエイティブを設定してください
                  </span>
                </template>
              </CreativeTable>
              <CreativeButtonAdd
                v-if="showCreativeButtonAdd"
                @onClick="showModal = true"
                :icon="editIcon"
              />
              <ModalEdit
                v-if="currentCampaignBudget && showModal"
                v-model="showModal"
                :creativeSchedule="currentCampaignBudgetCreativeSchedule"
                :resources="resources"
                :creative-type="form.campaign.creativeType"
                @apply="onChangeCreatives"
              />
            </div>
          </AppScrollContainer>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex>
      <Preview ref="preview" :current-creative="currentCreative" />
    </v-flex>
  </v-layout>
</template>

<script>
import { css } from 'emotion'
import dayjs from 'dayjs'
import union from 'lodash/union'
import sortBy from 'lodash/sortBy'
import unionBy from 'lodash/unionBy'
import { CreativeRepository } from '@/models/Creative'
import CampaignBudgetPeriodTable from './CampaignModalPageCreativeModeCampaignWithSchedulesCampaignBudgetPeriodTable'
import CampaignBudgetPeriodTableRow from './CampaignModalPageCreativeModeCampaignWithSchedulesCampaignBudgetPeriodTableRow'
import CreativeTable from './CampaignModalPageCreativeModeCampaignWithSchedulesCreativeTable'
import Preview from './CampaignModalPageCreativePreview'
import CreativeButtonAdd from './CampaignModalPageCreativeButtonAdd'
import ModalEdit from './CampaignModalPageCreativeModeCampaignWithSchedulesCreativeTableModalEdit'
import StatusSelect, {
  StatusOptions,
} from './CampaignModalPageCreativeStatusSelect'
import {
  actions as snackbarActions,
  snackbarType,
} from '@/vuex/modules/ui/snackbar/constants'
import { mapActions } from 'vuex'

export default {
  name: 'CampaignModalPageCreativeModeCampaignWithSchedules',
  components: {
    Preview,
    CampaignBudgetPeriodTable,
    CampaignBudgetPeriodTableRow,
    CreativeTable,
    CreativeButtonAdd,
    ModalEdit,
    StatusSelect,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentCreative: null,
      currentCampaignBudget: null,
      currentCampaignBudgetCreativeSchedule: null,
      height: 300,
      loading: false,
      showModal: false,
      optionForShowCreatives: StatusOptions.ACTIVE_ONLY,
      copiedCreatives: [],
    }
  },
  computed: {
    campaignBudgets() {
      return sortBy(this.form.getNoRejectedCampaignBudgets(), ['startTime'])
    },
    currentCampaignBudgetCreativeSchedules: {
      get() {
        if (!this.currentCampaignBudget) return []
        if (this.currentCampaignBudget.creativeSchedules.length === 0) {
          this.currentCampaignBudget.resetCreativeSchedules()
        }
        return this.currentCampaignBudget.creativeSchedules
      },
      set(creativeSchedules) {
        this.currentCampaignBudget.creativeSchedules = creativeSchedules
      },
    },
    currentCreatives: {
      get() {
        return this.currentCampaignBudgetCreativeSchedule
          ? this.currentCampaignBudgetCreativeSchedule.creatives
          : []
      },
      set(creatives) {
        this.currentCampaignBudgetCreativeSchedule.creatives = creatives
      },
    },
    isStartTimeEditable() {
      if (!this.currentCampaignBudgetCreativeSchedule) return false
      const now = dayjs()
      const startTime = this.currentCampaignBudgetCreativeSchedule.startTime
      return startTime
        ? dayjs(startTime).isAfter(now)
        : dayjs(this.currentCampaignBudget.startTime).isAfter(now)
    },
    isEndTimeEditable() {
      if (!this.currentCampaignBudgetCreativeSchedule) return false
      const now = dayjs()
      const endTime = this.currentCampaignBudgetCreativeSchedule.endTime
      return endTime
        ? dayjs(endTime).isAfter(now)
        : dayjs(this.currentCampaignBudget.endTime).isAfter(now)
    },
    fabContainerStyles() {
      return css`
        position: relative;
        height: 100%;
      `
    },
    editIcon() {
      return this.currentCreatives.length > 0 ? 'edit' : 'add'
    },
    resources() {
      return [this.form.advertiser, this.form.insertionOrder]
    },
    reloadable() {
      return this.visibleCreatives.length > 0 || this.currentCreative !== null
    },
    showCreativeButtonAdd() {
      return (
        this.currentCampaignBudget &&
        this.currentCampaignBudgetCreativeSchedule &&
        this.isEndTimeEditable &&
        this.currentCampaignBudgetCreativeSchedules.includes(
          this.currentCampaignBudgetCreativeSchedule,
        )
      )
    },
    visibleCreatives() {
      if (!this.currentCampaignBudgetCreativeSchedule) return []
      if (this.optionForShowCreatives === StatusOptions.ALL) {
        return this.currentCreatives
      } else {
        return this.currentCreatives.filter(c => c.isActive)
      }
    },
    disablePaste() {
      return (
        this.currentCampaignBudgetCreativeSchedule === null ||
        this.copiedCreatives.length === 0
      )
    },
    isGeoMenuSetting() {
      return this.form.campaign.hasGeoMenuSetting
    },
  },
  watch: {
    currentCampaignBudget() {
      if (this.currentCampaignBudget.length === 0) {
        this.currentCampaignBudget.resetCreativeSchedules()
      }
      this.currentCampaignBudgetCreativeSchedule = this.currentCampaignBudget.creativeSchedules[0]
    },
    currentCampaignBudgetCreativeSchedule() {
      this.currentCreative = null
      this.fetchCreatives()
    },
  },
  methods: {
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    isSelected(item) {
      return this.selectedCreatives.find(i => i.id === item.id) != null
    },
    setCurrentCampaignBudgetCreativeSchedule(campaignBudgetCreativeSchedule) {
      this.currentCampaignBudgetCreativeSchedule = campaignBudgetCreativeSchedule
    },
    sortDeliverySchedules() {
      const undefinedStartTimeCreativeSchedules = this.currentCampaignBudgetCreativeSchedules.filter(
        cs => !cs.startTime,
      )
      const existStartTimeCreativeSchedules = this.currentCampaignBudgetCreativeSchedules.filter(
        cs => cs.startTime,
      )
      this.currentCampaignBudgetCreativeSchedules = union(
        sortBy(undefinedStartTimeCreativeSchedules, ['endTime']),
        sortBy(existStartTimeCreativeSchedules, ['startTime']),
      )
    },
    async fetchCreatives() {
      if (!this.currentCampaignBudgetCreativeSchedule) return
      try {
        this.loading = true
        const creatives = await CreativeRepository.findByIdsWithFolderInfo(
          this.currentCreatives,
        )
        this.currentCreatives = creatives
      } catch (e) {
        console.error(e)
        throw new Error('クリエイティブ一覧の取得に失敗しました。')
      } finally {
        this.loading = false
      }
    },
    reloadCreative() {
      this.$refs.preview.reload()
    },
    reloadCreatives() {
      this.fetchCreatives()
      this.$emit('reload')
    },
    onChangeCreatives() {
      this.fetchCreatives()
    },
    copyCreatives(creatives) {
      this.copiedCreatives = creatives
      this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
        type: snackbarType.SUCCESS,
        message: `${creatives.length}件コピーしました。`,
      })
    },
    pasteCreatives() {
      const allCreativesCount =
        this.currentCreatives.length + this.copiedCreatives.length
      this.currentCreatives = unionBy(
        this.currentCreatives,
        this.copiedCreatives,
        'id',
      )
      const duplicateCount = allCreativesCount - this.currentCreatives.length
      this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
        type: snackbarType.SUCCESS,
        message: `${this.copiedCreatives.length}件ペーストしました。重複: ${duplicateCount}件`,
      })
    },
    removeAllCreatives() {
      this.form.campaign.removeCreatives()
      if (this.isGeoMenuSetting) {
        this.form.campaign.menuSetting.removeDeliveryCreatives()
      }
      this.currentCreatives = []
    },
  },
}
</script>

<style></style>
